/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\HttpServerField.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the httpServer module.
----------------------------------------------------------------*/


import React from 'react';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import { FieldWithReset } from './FieldWithReset';

interface HttpServerFieldProps {
  /** Unique field ID for the associated control */
  id: string;
  /** Display label text */
  label: string;
  /** Tooltip text shown on hover */
  tooltip: string;
  /** Whether the label requires the "required" indicator */
  required?: boolean;
  /** Current field value (used for dirty-check by FieldWithReset) */
  currentValue: unknown;
  /** Default value to restore on reset */
  defaultValue: unknown;
  /** Callback to reset the field to its default */
  onReset?: () => void;
  /** Tooltip text for the reset button */
  resetTooltip?: string;
  /** The actual control (Input, Toggle, select, etc.) */
  children: React.ReactNode;
}

/**
 * HttpServerField
 *
 * Renders one row in the 4-column HTTP Server settings grid:
 *   [Column 1] Tooltip-wrapped Label
 *   [Column 2] FieldWithReset wrapping the control
 *
 * Extracts the repeated SimpleTooltip + Label + FieldWithReset pattern
 * into a single reusable component.
 */
export function HttpServerField({
  id,
  label,
  tooltip,
  required = false,
  currentValue,
  defaultValue,
  onReset,
  resetTooltip,
  children,
}: HttpServerFieldProps) {
  return (
    <>
      <SimpleTooltip content={tooltip} title={label} position="top" maxWidth="300px">
        <Label htmlFor={id} required={required} className="text-left">
          {label}
        </Label>
      </SimpleTooltip>

      <FieldWithReset
        currentValue={currentValue}
        defaultValue={defaultValue}
        onReset={onReset}
        resetTooltip={resetTooltip}
      >
        {children}
      </FieldWithReset>
    </>
  );
}
